/**
 * Exponiert Entwickler-Hilfsfunktionen unter `window.dev` (z. B. fuer Eruda-Konsole).
 *
 * Nur in lokaler Umgebung aktiv. Backend-Endpunkte sind zusaetzlich ueber Spring-Profile
 * (`local|e2e|openapi-generation`) abgesichert und in Prod nicht erreichbar.
 *
 * Verwendung in Eruda-Konsole:
 *   await dev.login()           // Testnutzer anlegen + direkt einloggen (Onboarding noetig)
 *   await dev.loginOnboarded()  // Testnutzer komplett onboarded anlegen + einloggen
 *   await dev.clear()           // Datenbank zuruecksetzen (wie E2E-Reset)
 *   dev.help()
 */
export default defineNuxtPlugin(() => {
	const { isLocal } = useEnvironment()
	if (!isLocal) return

	const { $apiWithoutChecks } = useNuxtApp()
	const { applyExternalTokens } = useAuth()
	const { refresh } = useApp()

	interface TestUserLoginData {
		userId: string
		email: string
		accessToken: string
		idToken: string
		refreshToken: string
		expiresIn: number
		refreshExpiresIn: number
	}
	interface DevApi {
		reset: () => Promise<unknown>
		createAndLoginTestUser: () => Promise<{ data: TestUserLoginData }>
		createAndLoginOnboardedTestUser: () => Promise<{ data: TestUserLoginData }>
	}
	const api = () => ($apiWithoutChecks as { devApi: DevApi }).devApi

	const clear = () =>
		handleAsync(async () => {
			await api().reset()
			console.info('[dev] Datenbank zurueckgesetzt')
		}, 'Datenbank-Reset fehlgeschlagen')

	const loginWith = (call: () => Promise<{ data: TestUserLoginData }>, label: string) =>
		handleAsync(async () => {
			const { data } = await call()
			await applyExternalTokens({
				accessToken: data.accessToken,
				idToken: data.idToken,
				refreshToken: data.refreshToken,
			})
			await refresh()
			await navigateTo(RouteNames.INDEX, { replace: true })
			console.info(`[dev] ${label} ${data.email} (UUID=${data.userId})`)
		}, `${label} fehlgeschlagen`)

	const login = () =>
		loginWith(() => api().createAndLoginTestUser(), 'Eingeloggt als')

	const loginOnboarded = () =>
		loginWith(() => api().createAndLoginOnboardedTestUser(), 'Onboarded eingeloggt als')

	const help = () => {
		console.info(
			'%c[dev] Befehle',
			'font-weight:bold',
			'\n  dev.login()           – Testnutzer anlegen + einloggen (Onboarding noetig)',
			'\n  dev.loginOnboarded()  – Komplett onboardeten Testnutzer anlegen + einloggen',
			'\n  dev.clear()           – Datenbank zuruecksetzen (wie E2E-Reset)',
			'\n  dev.help()            – Diese Hilfe',
		)
	}

	const dev = { login, loginOnboarded, clear, help }
	;(window as unknown as { dev: typeof dev }).dev = dev
	console.info('[dev] Konsolen-Befehle bereit – `dev.help()` fuer Uebersicht.')
})
